import { useState, useEffect } from 'react';
import supabase from '../../supabase-client';

// Helper para ordenar las categorías alfabéticamente, asegurando consistencia.
const sortCategoriesAlphabetically = (categories) => {
  return [...categories].sort((a, b) => a.name_category.localeCompare(b.name_category));
};

export const useCategories = () => {
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Cargar todas las categorías usando SQL directo
  const fetchCategories = async () => {
    try {
      setLoading(true);
      setError(null);

      const { data, error: dbError } = await supabase.rpc('get_all_categories');

      if (dbError) throw new Error(dbError.message);
      
      // Mapear los datos para que coincidan con el formato esperado
      const mappedData = (data || []).map(item => ({
        id_category: item.cat_id,
        name_category: item.cat_name
      }));
      
      setCategories(mappedData);
    } catch (err) {
      console.error('Error al cargar categorías:', err.message);
      setError('No se pudieron cargar las categorías');
    } finally {
      setLoading(false);
    }
  };

  // Crear nueva categoría usando SQL directo
  const addCategory = async ({ name_category }) => {
    try {
      setError(null);

      const name = name_category?.trim();
      if (!name) throw new Error('El nombre de la categoría es requerido');

      const { data, error: dbError } = await supabase.rpc('create_category', {
        p_category_name: name
      });

      if (dbError) throw new Error(dbError.message);
      if (!data) throw new Error('No se pudo crear la categoría');

      // Recargar categorías para mantener sincronización
      await fetchCategories();
      return data;
    } catch (err) {
      console.error('Error al crear categoría:', err.message);
      setError(err.message);
      throw err;
    }
  };

  // Actualizar categoría usando SQL directo
  const updateCategory = async (id_category, { name_category }) => {
    try {
      setError(null);

      const name = name_category?.trim();
      if (!name) throw new Error('El nombre de la categoría es requerido');

      const { data, error: dbError } = await supabase.rpc('update_category', {
        p_category_id: id_category,
        p_new_name: name
      });

      if (dbError) throw new Error(dbError.message);
      if (!data) throw new Error('No se pudo actualizar la categoría');

      // Recargar categorías para mantener sincronización
      await fetchCategories();
      return data;
    } catch (err) {
      console.error('Error al actualizar categoría:', err.message);
      setError(err.message);
      throw err;
    }
  };

  // Eliminar categoría usando SQL directo
  const deleteCategory = async (categoryId, categoryName) => {
    try {
      // Verificar directamente si la categoría está siendo usada por algún libro
      const { data: booksUsingCategory, error: depError } = await supabase
        .from('productbook')
        .select('title_product')
        .contains('categories_product', [categoryId]);

      if (depError) throw new Error(depError.message);

      const isUsed = booksUsingCategory && booksUsingCategory.length > 0;
      const bookTitles = booksUsingCategory?.map(book => book.title_product) || [];

      if (isUsed && bookTitles.length > 0) {
        // Mostrar modal de error si está siendo usada
        return new Promise((resolve, reject) => {
          const modalContainer = document.createElement('div');
          modalContainer.className = 'fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4';
          
          modalContainer.innerHTML = `
            <div class="bg-white rounded-lg shadow-lg border border-gray-300 w-full max-w-md p-6">
              <div class="flex justify-center mb-4">
                <img src="/assets/images/favicon.png" alt="Logo" class="w-12 h-12" onerror="this.src='/src/assets/favicon.png'"/>
              </div>
              <div class="text-center mb-6">
                <p class="text-gray-700 mb-2">No se puede eliminar la categoría</p>
                <p class="font-medium text-gray-900 mb-2">"${categoryName}"</p>
                <p class="text-gray-600 text-sm mb-3">El libro "${bookTitles[0]}" lo está usando.</p>
                <p class="text-gray-600 text-sm">Debe quitarlo del libro primero.</p>
              </div>
              <div class="flex justify-center">
                <button id="ok-btn" class="px-6 py-2 text-white bg-blue-600 rounded hover:bg-blue-700 transition-colors">
                  Entendido
                </button>
              </div>
            </div>
          `;
          
          document.body.appendChild(modalContainer);
          
          const okBtn = modalContainer.querySelector('#ok-btn');
          
          okBtn.onclick = () => {
            document.body.removeChild(modalContainer);
            reject(new Error('No se puede eliminar: categoría en uso'));
          };
        });
      }

      // Si no está siendo usada, mostrar confirmación normal
      return new Promise((resolve, reject) => {
        const modalContainer = document.createElement('div');
        modalContainer.className = 'fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4';
        
        modalContainer.innerHTML = `
          <div class="bg-white rounded-lg shadow-lg border border-gray-300 w-full max-w-md p-6">
            <div class="flex justify-center mb-4">
              <img src="/assets/images/favicon.png" alt="Logo" class="w-12 h-12" onerror="this.src='/src/assets/favicon.png'"/>
            </div>
            <div class="text-center mb-6">
              <p class="text-gray-700 mb-2">¿Quiere eliminar la categoría</p>
              <p class="font-medium text-gray-900 mb-2">"${categoryName}"?</p>
            </div>
            <div class="flex gap-3 justify-center">
              <button id="cancel-btn" class="px-6 py-2 text-gray-700 border border-gray-300 rounded hover:bg-gray-50 transition-colors">
                Cancelar
              </button>
              <button id="confirm-btn" class="px-6 py-2 text-white bg-red-600 rounded hover:bg-red-700 transition-colors">
                Aceptar
              </button>
            </div>
          </div>
        `;
        
        document.body.appendChild(modalContainer);
        
        const cancelBtn = modalContainer.querySelector('#cancel-btn');
        const confirmBtn = modalContainer.querySelector('#confirm-btn');
        
        const cleanup = () => {
          document.body.removeChild(modalContainer);
        };
        
        cancelBtn.onclick = () => {
          cleanup();
          reject(new Error('Eliminación cancelada'));
        };
        
        confirmBtn.onclick = async () => {
          confirmBtn.textContent = 'Eliminando...';
          confirmBtn.disabled = true;
          
          try {
            setError(null);
            
            const { data, error } = await supabase.rpc('delete_category', {
              p_category_id: categoryId
            });

            if (error) throw new Error(error.message);
            if (!data) throw new Error('No se pudo eliminar la categoría');

            await fetchCategories();
            cleanup();
            resolve(true);
          } catch (err) {
            console.error('Error deleting category:', err);
            setError(err.message);
            cleanup();
            reject(err);
          }
        };
      });
    } catch (err) {
      console.error('Error checking category dependencies:', err);
      setError(err.message);
      throw err;
    }
  };

  // Limpiar error
  const clearError = () => setError(null);

  // Carga inicial
  useEffect(() => {
    fetchCategories();
  }, []);

  return {
    categories,
    loading,
    error,
    addCategory,
    updateCategory,
    deleteCategory,
    clearError,
  };
};
export default useCategories;
